import { axiosAPIInstance as axios } from '../../axios-config';
import getParameterByName from '../../utils/query-params';

const state = {
  showAdmin: false,
  email: '',
  authToken: '',
  anonymousIdValue: null,
  me: {}
};

const mutations = {
  setEmail(state, { email }) { state.email = email; },
  setAuthToken(state, { authToken }) { state.authToken = authToken; },
  setMe(state, { me }) { state.me = me; },
  setShowAdmin(state, { showAdmin }) {
    state.showAdmin = !!showAdmin;
  },
  setAidParam(state, param) {
    state.anonymousIdValue = param;
  }
};

const actions = {
  clearEmail({ commit }) {
    commit('setEmail', { email: '' });
  },
  initializeAnonymousAuthParams({ commit }) {
    const AIDParam = getParameterByName('AID') || getParameterByName('aid');
    if (AIDParam) {
      commit('setAidParam', AIDParam);

      // Remove the auth token
      localStorage.removeItem('authToken');
    }
  },
  populateAuthToken({ commit, rootGetters: { isLti, ltiAuthToken } }) {
    if (isLti) {
      commit('setAuthToken', { authToken: ltiAuthToken });
    } else {
      const authToken = window.localStorage.getItem('authToken') || '';
      commit('setAuthToken', { authToken });
    }
  },
  storeAuthToken({ commit, rootGetters: { isLti } }, { authToken }) {
    if (isLti) return;

    commit('setAuthToken', { authToken });
    window.localStorage.setItem('authToken', authToken);
  },
  async renewAuthToken({
    commit,
    // dispatch
  }) {
    const { data: { showAdmin, user: me } } = await axios.get('/users/me/token');
    commit('setShowAdmin', { showAdmin });
    commit('setMe', { me });
    // dispatch('initializeSocket');
    return Promise.resolve();
  },
  async emailExists({ commit }, { email }) {
    commit('setEmail', { email });
    await axios.get('/users/email', { params: { email } });
    return Promise.resolve();
  },
  async login({
    state,
    commit,
    // dispatch
  }, {
    loginUniqueId,
    email,
    password,
    classId,
    code,
    role,
    registrationLink,
  }) {
    let tgUrl = '/users/login';
    if (classId) {
      tgUrl = `/users/login/class/${classId}`;
    }
    if (code) {
      tgUrl = `/users/login/game/${code}`;
      if (state.me && state.me.email) {
        tgUrl = `/users/addgame/${code}`;
      }
    }
    if (registrationLink) {
      tgUrl = `/users/login/registration-link/${registrationLink}`;
    }

    // const { data: { showAdmin, user: me } } =
    await axios.post(
      tgUrl,
      { loginUniqueId, email, password, role }
    );
    const { data: { showAdmin, user: me } } = await axios.get('/users/me/token');

    commit('setShowAdmin', { showAdmin });
    commit('setMe', { me });
    // dispatch('initializeSocket');
    return Promise.resolve();
  },
  async signup({
    state,
    commit,
    // dispatch
  }, {
    firstName,
    lastName,
    email,
    password,
    classId,
    studentId,
    code,
    registrationLink,
    role,
  }) {
    let tgUrl = '/users';
    if (classId) {
      tgUrl = `/users/class/${classId}`;
    }
    if (code) {
      tgUrl = `/users/game/${code}`;
      if (state.me && state.me.email) {
        tgUrl = `/users/addgame/${code}`;
      }
    }
    if (registrationLink) {
      tgUrl = `/users/registration-link/${registrationLink}`;
      if (state.me && state.me.email) {
        tgUrl = `/users/add/registration-link/${registrationLink}`;
      }
    }

    const { data: { showAdmin, user: me } } = await axios.post(tgUrl,
      { firstName, lastName, email, password, studentId, role });

    if (!(state.me) || me) commit('setShowAdmin', { showAdmin });
    if (!(state.me) || me) commit('setMe', { me });
  },
  logout({ commit, dispatch }) {
    commit('setMe', { me: {} });
    commit('setAuthToken', { authToken: '' });
    window.localStorage.removeItem('authToken');
    dispatch('disconnectSocket');
  },
  async requestPasswordResetEmail(_, { email }) {
    await axios.patch('/user/forgotPassword', { email });
  },
  async resetPassword(_, { id, newPassword }) {
    await axios.patch('/user/resetPassword', { id, newPassword });
  },
  async requestVerificationEmail(_, { email }) {
    await axios.patch('/user/requestVerification', { email });
  },
  async verifyEmail(_, { code }) {
    await axios.patch('/user/verifyEmail', { code });
  }
};

const getters = {
  // Not entirely sure the purpose of showAdmin...
  // I think this is some sort of override for production environments to
  // hide the Admin dropdown no matter your user role? This seems like a strange choice.
  showAdmin({ showAdmin }) { return showAdmin; },
  email({ email }) { return email; },
  authToken({ authToken }) { return authToken; },
  me({ me }) { return me; },
  muteMusic({ me }) { return me.muteMusic || false; },
  volumeMusic({ me }) { return me.volumeMusic || 0; },
  muteVoices({ me }) { return me.muteVoices || false; },
  volumeVoices({ me }) { return me.volumeVoices || 0; },
  muteSFX({ me }) { return me.muteSFX || false; },
  volumeSFX({ me }) { return me.volumeSFX || 0; },
  hasAuthToken({ authToken }) {
    return !!authToken;
  },
  canDeepDelete({ me: { roles = [] } }) {
    const allowedRoles =
      ['owner'];
    return allowedRoles.some(role => roles.some(({ _id }) => _id === role));
  },
  canManageGames({ me: { roles = [] } }) {
    const allowedRoles =
      ['owner', 'developer'];
    return allowedRoles.some(role => roles.some(({ _id }) => _id === role));
  },
  canManageDashboards({ me: { roles = [] } }, getters, rootState, rootGetters) {
    const allowedRoles =
      ['owner'];

    // If dashboards aren't enabled, nobody should have permission to access them
    return rootGetters.dashboardsEnabled && allowedRoles.some(role => roles.some(({ _id }) => _id === role));
  },
  canManageDashboardUserExclusionList({ me: { roles = [] } }, getters, rootState, rootGetters) {
    const allowedRoles =
      ['owner', 'admin-content'];

    // If dashboards aren't enabled, nobody should have permission to access them
    return rootGetters.dashboardsEnabled && allowedRoles.some(role => roles.some(({ _id }) => _id === role));
  },
  canEditGameGeneralSettings({ me: { roles = [] } }) {
    const allowedRoles =
      ['blade', 'owner', 'developer', 'admin-content'];
    return allowedRoles.some(role => roles.some(({ _id }) => _id === role));
  },
  canManageGameSets({ me: { roles = [] } }) {
    const allowedRoles =
      ['blade', 'owner', 'admin', 'admin-content'];
    return allowedRoles.some(role => roles.some(({ _id }) => _id === role));
  },
  canManageGameInvites({ me: { roles = [] } }) {
    const allowedRoles =
      ['blade', 'owner', 'developer', 'admin', 'admin-content'];
    return allowedRoles.some(role => roles.some(({ _id }) => _id === role));
  },
  canManageEnvironments({ me: { roles = [] } }) { // "Environments" == "Gamulation Brandings"
    const allowedRoles =
      ['blade', 'owner', 'developer', 'admin', 'admin-content'];
    return allowedRoles.some(role => roles.some(({ _id }) => _id === role));
  },
  canListOutros({ me: { roles = [] } }) {
    const allowedRoles =
      ['blade', 'owner', 'developer', 'admin', 'admin-content'];
    return allowedRoles.some(role => roles.some(({ _id }) => _id === role));
  },
  canViewDashboards({ me: { roles = [], forceCanViewDashboards } }) {
    const allowedRoles =
      ['blade', 'owner', 'admin', 'admin-content'];
    return forceCanViewDashboards
      || allowedRoles.some(role => roles.some(({ _id }) => _id === role));
  },
  canManageClasses({ me: { roles = [] } }) {
    const allowedRoles =
      ['blade', 'professor', 'manager', 'owner', 'developer', 'admin', 'admin-content', 'agent'];
    return allowedRoles.some(role => roles.some(({ _id }) => _id === role));
  },
  canManageUsers({ me: { roles = [] } }) {
    const allowedRoles =
      ['blade', 'owner', 'admin', 'admin-content'];
    return allowedRoles.some(role => roles.some(({ _id }) => _id === role));
  },
  canGenerateReports({ me: { roles = [] } }) {
    const allowedRoles =
      ['blade', 'owner', 'admin', 'admin-content', 'professor', 'manager'];
    return allowedRoles.some(role => roles.some(({ _id }) => _id === role));
  },
  canManageAllReportConfigurations({ me: { roles = [] } }) {
    const allowedRoles =
      ['blade', 'owner', 'admin', 'admin-content'];
    return allowedRoles.some(role => roles.some(({ _id }) => _id === role));
  },
  /** referring to global groups, rather than class groups */
  canManageAllGroups({ me: { roles = [] } }) {
    const allowedRoles =
      ['blade', 'owner', 'developer'];
    return allowedRoles.some(role => roles.some(({ _id }) => _id === role));
  },
  canUseSaveStates({ me: { roles = [] } }) {
    const allowedRoles =
      ['blade', 'owner', 'developer'];
    return allowedRoles.some(role => roles.some(({ _id }) => _id === role));
  },
  canSelectGame({ me: { roles = [] } }) {
    const allowedRoles =
      ['blade', 'demo', 'owner', 'admin', 'admin-content', 'developer'];
    return allowedRoles.some(role => roles.some(({ _id }) => _id === role));
  },
  canRestartGame({ me: { roles = [] } }) {
    const allowedRoles =
      ['blade', 'demo', 'owner', 'admin', 'admin-content', 'developer', 'client', 'professor', 'manager'];
    return allowedRoles.some(role => roles.some(({ _id }) => _id === role));
  },
  canViewDemo({ me: { roles = [] } }) {
    const allowedRoles =
      ['blade', 'demo', 'owner', 'admin', 'admin-content', 'developer', 'client', 'professor', 'manager', 'employee', 'student'];
    return allowedRoles.some(role => roles.some(({ _id }) => _id === role));
  },
  // TODO: new privilege system will replace this **************************************************
  isProfessor({ me: { roles = [] } }) {
    // TODO: manager alias of professor for now
    return roles.some(({ _id }) => _id === 'professor' || _id === 'manager');
  },
  /** NOTE: for now, manager is an alias for professor, but professor is NOT an alias for manager */
  isManager({ me: { roles = [] } }) {
    return roles.some(({ _id }) => _id === 'manager');
  },
  isNonBladeAdmin({ me: { roles = [] } }) {
    return ['admin', 'admin-content'].some(role => roles.some(({ _id }) => _id === role));
  },
  isNonBladeAdminContent({ me: { roles = [] } }) {
    return ['admin-content'].some(role => roles.some(({ _id }) => _id === role));
  },
  isOwner({ me: { roles = [] } }) {
    return ['owner'].some(role => roles.some(({ _id }) => _id === role));
  },
  isOwnerOrBladeAdmin({ me: { roles = [] } }) {
    return ['blade', 'owner'].some(role => roles.some(({ _id }) => _id === role));
  },
  isOwnerOrAdmin({ me: { roles = [] } }) {
    return ['blade', 'owner', 'admin', 'admin-content'].some(role => roles.some(({ _id }) => _id === role));
  },
  isOwnerAdminOrProfessor({ me: { roles = [] } }) {
    // TODO: manager alias of professor for now
    return ['blade', 'owner', 'admin', 'admin-content', 'professor', 'manager'].some(role => roles.some(({ _id }) => _id === role));
  },
  isOwnerAdminDeveloperOrProfessor({ me: { roles = [] } }) {
    // TODO: manager alias of professor for now
    return ['blade', 'owner', 'admin', 'admin-content', 'professor', 'manager', 'developer'].some(role => roles.some(({ _id }) => _id === role));
  },
  isStudent({ me: { roles = [] } }) {
    return ['student'].some(role => roles.some(({ _id }) => _id === role));
  },
  isStudentOrEmployee({ me: { roles = [] } }) {
    return ['student', 'employee'].some(role => roles.some(({ _id }) => _id === role));
  },
  isStudentOrProfessor({ me: { roles = [] } }) {
    // TODO: manager alias of professor for now
    return ['student', 'professor', 'manager'].some(role => roles.some(({ _id }) => _id === role));
  },
  isCorpo({ me: { roles = [] } }) {
    // console.log(roles);
    return ['admin', 'admin-content', 'employee', 'manager'].some(role => roles.some(({ _id }) => _id === role));
  },
  isInternalCorpo({ me: { roles = [] } }) {
    return ['blade', 'owner', 'developer'].some(role => roles.some(({ _id }) => _id === role));
  },
  classTypeName(_, { isCorpo, isInternalCorpo }, rootState, rootGetters) {
    const config = rootGetters.activeSignInConfig;
    if (config && config.classWorkspaceLabelOverride) {
      return config.classWorkspaceLabelOverride;
    }
    return isCorpo ? 'Workgroup' : (isInternalCorpo ? 'Class/Workgroup' : 'Class');
  },
  classTypeNamePlural(_, { isCorpo, isInternalCorpo }, rootState, rootGetters) {
    const config = rootGetters.activeSignInConfig;
    if (config) {
      if (config.classWorkspaceLabelOverridePlural) {
        return config.classWorkspaceLabelOverridePlural;
      } else if (config.classWorkspaceLabelOverride) {
        return `${config.classWorkspaceLabelOverride}s`;
      }
    }
    return isCorpo ? 'Workgroups' : (isInternalCorpo ? 'Classes/Workgroups' : 'Classes');
  },
  classTypeNameLower(_, { isCorpo, isInternalCorpo }, rootState, rootGetters) {
    const config = rootGetters.activeSignInConfig;
    if (config && config.classWorkspaceLabelOverride) {
      return config.classWorkspaceLabelOverride.toLowerCase();
    }
    return isCorpo ? 'workgroup' : (isInternalCorpo ? 'class/workgroup' : 'class');
  },
  classTypeNameLowerPlural(_, { isCorpo, isInternalCorpo }, rootState, rootGetters) {
    const config = rootGetters.activeSignInConfig;
    if (config && config.classWorkspaceLabelOverride) {
      if (config.classWorkspaceLabelOverridePlural) {
        return config.classWorkspaceLabelOverridePlural.toLowerCase();
      }
      return `${config.classWorkspaceLabelOverride.toLowerCase()}s`;
    }
    return isCorpo ? 'workgroups' : (isInternalCorpo ? 'classes/workgroups' : 'classes');
  },
  // TODO: new privilege system will replace the above *********************************************
  hasAnonymousIdParam({ anonymousIdValue }) {
    return !!anonymousIdValue;
  },
  anonymousIdParam({ anonymousIdValue }) {
    return anonymousIdValue;
  }
};

export default {
  state,
  mutations,
  actions,
  getters
};



// WEBPACK FOOTER //
// ./src/store/auth/auth.js